import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ADS_LAUNCH_STEP_STATUSES, } from '@wit-support/admin-contracts';
import { STEP_UNKNOWN_TEXT, stepStatusLabel } from '../step-status.js';
import { Badge } from '@wit-support/ui';
import { EntityId } from '@wit-support/ui-domain';
import { AdsTime } from './ads-time.js';
import { isMediaErrorCode, launchErrorText, mediaErrorText } from '../media-errors.js';
import { localRefLabel, metaObjectKind, stepLabelRu } from '../naming.js';
/** Шаг переноса файла (`media.image`, `media.video`) — его ошибка словами. */
const isMediaStep = (kind, code) => kind.startsWith('media') || isMediaErrorCode(code);
/** Прежние имена статусов (старый auto-ads) — те же подписи. */
const LEGACY_STATUS = {
    running: 'in_progress',
    succeeded: 'done',
};
/** Пояснения auto-ads к статусу шага (`note`) — главнее голого статуса. */
const NOTE_RU = {
    'already-uploaded': { label: 'уже загружено в кабинет', tone: 'success' },
};
/**
 * Статус шага словами: пояснение (`note`) главнее статуса — пропущенная
 * загрузка файла, который уже лежит в кабинете, это «уже загружено в
 * кабинет», а не «пропущено». Незнакомый статус — «статус неизвестен».
 */
export function stepStatusView(s, launchStatus) {
    const byNote = s.note != null ? NOTE_RU[s.note] : undefined;
    if (byNote)
        return byNote;
    const status = ADS_LAUNCH_STEP_STATUSES.includes(s.status)
        ? s.status
        : LEGACY_STATUS[s.status];
    // запуск откатан / откатывается: невыполненный шаг уже не выполнится — не «ждёт» (E-2 P2-9)
    if (status === 'pending' && launchStatus === 'rolled_back')
        return STEP_NOT_RUN_ROLLED_BACK;
    if (status === 'pending' && launchStatus === 'rolling_back')
        return STEP_NOT_RUN_ROLLING_BACK;
    // незнакомый статус — нейтрально и без голого кода
    return status ? stepStatusLabel(status, s.kind) : { label: STEP_UNKNOWN_TEXT, tone: 'neutral' };
}
/** Шаг не выполнялся: запуск откатан раньше, чем до него дошло. */
export const STEP_NOT_RUN_ROLLED_BACK = {
    label: 'не выполнялся — запуск откатан',
    tone: 'neutral',
};
export const STEP_NOT_RUN_ROLLING_BACK = {
    label: 'не выполнится — запуск откатывается',
    tone: 'neutral',
};
const SET_OF_REF_RE = /^s(\d+)\//;
/**
 * Подпись объекта шага: с группами шаблона — «① LAL 1 % · Креатив 1»; без них
 * креативы разных групп объявлений различаются группой («Группа объявлений 2 ·
 * Креатив 1», а не два «Креатив 1» подряд). Подпись, совпавшая с названием
 * шага («Кампания · Кампания»), не повторяется (E-2 P3).
 */
export function stepRefLabel(s, steps, groups = null) {
    let ref = localRefLabel(s.localRef, s.kind, groups);
    if ((groups == null || groups.length === 0) &&
        s.localRef != null &&
        s.kind.split('.').pop() === 'creative') {
        const sets = new Set(steps
            .filter((x) => x.kind === s.kind)
            .map((x) => SET_OF_REF_RE.exec(x.localRef ?? '')?.[1])
            .filter((x) => x != null));
        const set = SET_OF_REF_RE.exec(s.localRef)?.[1];
        if (sets.size > 1 && set != null)
            ref = `Группа объявлений ${Number(set) + 1} · ${ref}`;
    }
    return ref === stepLabelRu(s.kind) ? '' : ref;
}
/** Лента шагов запуска: метка → медиа → креативы → кампания → группы → объявления. */
export function StepsTimeline({ steps, emptyText = 'Шаги ещё не начаты.', launchStatus = null, groups = null, }) {
    if (steps.length === 0) {
        return (_jsx("span", { className: "text-sm text-[var(--color-text-muted)]", "data-testid": "steps-empty", children: emptyText }));
    }
    return (_jsx("ol", { className: "flex flex-col gap-2", "data-testid": "steps", children: [...steps]
            .sort((a, b) => a.seq - b.seq)
            .map((s) => {
            const st = stepStatusView(s, launchStatus);
            const ref = stepRefLabel(s, steps, groups);
            return (_jsxs("li", { className: "flex flex-col gap-0.5 text-sm", children: [_jsxs("div", { className: "group/entity flex flex-wrap items-center gap-2", children: [_jsx("span", { className: "w-6 text-right text-xs tabular-nums text-[var(--color-text-muted)]", children: s.seq }), _jsx("span", { className: "font-medium", title: `${s.kind}${s.localRef ? ` · ${s.localRef}` : ''}`, "data-testid": `step-${s.seq}-label`, children: stepLabelRu(s.kind) }), ref !== '' ? (_jsx("span", { className: "text-xs text-[var(--color-text-secondary)]", children: ref })) : null, _jsx(Badge, { variant: st.tone, children: st.label }), s.metaId != null ? _jsx(EntityId, { id: s.metaId, kind: metaObjectKind(s.kind) }) : null, s.attempts > 1 ? (_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u043F\u043E\u043F\u044B\u0442\u043E\u043A: ", s.attempts] })) : null, s.finishedAt != null ? (_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: _jsx(AdsTime, { iso: s.finishedAt, preset: "time" }) })) : null] }), s.error ? (_jsx(StepErrorText, { error: s.error, isMedia: isMediaStep(s.kind, s.error.code) })) : null] }, s.seq));
        }) }));
}
/**
 * Ошибка шага словами. Оригинал, код и fbtrace_id — только в раскрывашке
 * «Для поддержки» (приёмка E-2, P2-10): человеку по-русски, без «код 100».
 */
function StepErrorText({ error, isMedia, }) {
    const h = isMedia ? mediaErrorText(error) : launchErrorText(error);
    const hasSupport = h.detail != null || error.fbtraceId != null;
    return (_jsxs("div", { className: "pl-8 text-xs text-[var(--color-state-danger-fg)] [overflow-wrap:anywhere]", children: [h.text, hasSupport ? (_jsxs("details", { className: "text-[var(--color-text-muted)]", children: [_jsx("summary", { className: "cursor-pointer", children: "\u0414\u043B\u044F \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0438" }), h.detail != null ? _jsx("div", { children: h.detail }) : null, error.fbtraceId ? _jsxs("div", { children: ["fbtrace_id ", error.fbtraceId] }) : null] })) : null] }));
}
//# sourceMappingURL=steps-timeline.js.map